<template>
  <form name="contract" class="needs-validation" novalidate>
    <div id="alert" class="alert alert-dismissible fade show m-alert m-alert--outline mb-4 mt-4" :class="alert.color"
      role="alert" v-if="alert.message" ref="alert">
      <button type="button" class="close" aria-label="Close" @click="alert.message = ''"></button>
      <i :class="alert.icon"></i> &nbsp;<strong>{{ alert.message }}</strong><br />
      {{ alert.reason }}
    </div>

    <div class="row mt-3">
      <div class="col-md-12 mb-3" v-show="false">
        <div class="form-check">
          <input class="form-check-input" type="checkbox" id="applyToAllContracts" v-model="data.applyToAllContracts" />
          <label class="form-check-label" for="applyToAllContracts">
            Aplicar reajuste para todos os contratos até 07/2024
          </label>
        </div>
      </div>
      <div class="col-md-7">
        <div class="form-group"
          :class="{ 'has-danger': !data.actual_date_birthday_month && !data.applyToAllContracts }">
          <label>Mês de aniversário da data atual do contrato
            <span v-if="!data.id && !data.applyToAllContracts" class="m--font-danger">*</span></label>
          <div class="input-group">
            <SelectMonth ref="actual_date_birthday_month" :selected="data.actual_date_birthday_month"
              :first="'Selecione um Mês'" @change="onMonthChange"
              :disabled="!!data.id || loadingContracts || data.applyToAllContracts" />
          </div>
        </div>
      </div>
      <div class="col-md-5">
        <div class="form-group" :class="{ 'has-danger': !data.percent }">
          <label for="percent">Porcentagem de reajuste <span class="m--font-danger">* </span></label>
          <div class="input-group">
            <input id="percent" type="number" class="form-control m-input" ref="percent" v-model="data.percent"
              required />
          </div>
        </div>
      </div>
    </div>

    <div v-if="showContractList && !data.applyToAllContracts" class="row mt-4">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header">
            <h5 class="card-title">
              Contratos para o mês selecionado
            </h5>
          </div>
          <div class="card-body">
            <div v-if="loadingContracts" class="text-center">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
              <p class="mt-2">Carregando contratos...</p>
            </div>
            <div v-else-if="contractList.length === 0" class="text-center">
              <p>Nenhum contrato encontrado para o mês selecionado.</p>
            </div>
            <div v-else>
              <div v-if="selectedContractIds.length > 0" class="mb-3">
                <div class="alert alert-primary m-alert m-alert--outline">
                  <i class="fas fa-info-circle mr-2"></i>
                  <strong>{{ selectedContractIds.length }}</strong> contrato(s) selecionado(s) para o reajuste
                  específico.
                </div>
              </div>
              <div v-else-if="contractList.length > 0" class="mb-3">
                <div class="alert alert-warning m-alert m-alert--outline">
                  <i class="fas fa-exclamation-triangle mr-2"></i>
                  Nenhum contrato selecionado. O reajuste será aplicado a <strong>todos os contratos</strong> do mês.
                </div>
              </div>

              <div class="table-responsive">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th width="50">
                        <label class="m-checkbox m-checkbox--single m-checkbox--solid m-checkbox--brand mb-0 mt-0"
                          v-b-tooltip.hover title="Selecionar todos os contratos">
                          <input class="m-group-checkable" type="checkbox" :checked="allContractsSelected"
                            @change="toggleSelectAll" />
                          <span></span>
                        </label>
                      </th>
                      <th>Cliente</th>
                      <th>Status</th>
                      <th>Documento</th>
                      <th>ID do Contrato</th>
                      <th>Data atual do Contrato</th>
                      <th>Data mais antiga de atualização de um serviço</th>
                      <th>Último Reajuste pelo Sistema</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="contract in paginatedContracts" :key="contract.contract_id">
                      <td>
                        <label class="m-checkbox m-checkbox--single m-checkbox--solid m-checkbox--brand mb-0 mt-0"
                          v-b-tooltip.hover.right
                          :title="`Contrato #${contract.contract_id} - Selecionar este contrato`">
                          <input class="m-group-checkable" type="checkbox" :value="contract.contract_id"
                            v-model="selectedContractIds" />
                          <span></span>
                        </label>
                      </td>
                      <td>{{ contract.name }}</td>
                      <td>
                        <span :class="getCustomerStatusClass(contract.customer_status)">
                          {{ getCustomerStatusText(contract.customer_status) }}
                        </span>
                      </td>
                      <td>{{ contract.document | cnpj | cpf }}</td>
                      <td>{{ contract.contract_id }}</td>
                      <td>{{ contract.actual_contract_date | date }}</td>
                      <td>{{ contract.min_updated_at | date }}</td>
                      <td>{{ formatLastReadjustment(contract) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <nav v-if="totalPages > 1" aria-label="Contract pagination">
                <ul class="pagination justify-content-center">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">
                      Anterior
                    </a>
                  </li>
                  <li v-for="page in visiblePages" :key="page" class="page-item"
                    :class="{ active: page === currentPage }">
                    <a class="page-link" href="#" @click.prevent="changePage(page)">
                      {{ page }}
                    </a>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">
                      Próximo
                    </a>
                  </li>
                </ul>
              </nav>

              <div class="text-center mt-2">
                <small class="text-muted">
                  Mostrando {{ ((currentPage - 1) * itemsPerPage) + 1 }} a
                  {{ Math.min(currentPage * itemsPerPage, contractList.length) }}
                  de {{ contractList.length }} contratos
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <hr class="row mt-5 mb-3" />
    <div class="row">
      <div class="col-md-12 text-right">
        <p class="p-0 m-0">
          <a href="#" @click.prevent="$refs.actual_date_birthday_month.$el.focus()" target="_self"
            class="m--font-danger" v-if="!data.actual_date_birthday_month && !data.applyToAllContracts">O campo
            <strong>Mês de aniversário da data atual do contrato</strong> é obrigatório
            *</a>
        </p>
        <p class="p-0 m-0">
          <a href="#" @click.prevent="$refs.percent.focus()" target="_self" class="m--font-danger"
            v-if="!data.percent">O campo <strong>Porcentagem de reajuste</strong> é obrigatório *</a>
        </p>
      </div>
    </div>
    <div class="row mb-5">
      <div class="col-md-12 text-right">
        <button @click.prevent="saveForm()" class="btn" :class="{ 'btn-metal': !formIsValid, 'btn-focus': formIsValid }"
          :disabled="!formIsValid || loading || loadingContracts">
          <i class="fas fa-save mr-2" v-if="formIsValid && !loading && !loadingContracts"></i>
          <i class="fas fa-spinner fa-spin mr-2" v-else-if="loading || loadingContracts"></i>
          <i class="fas fa-exclamation-triangle mr-2" v-else></i>
          <span v-if="loadingContracts">Carregando...</span>
          <span v-else>Salvar</span>
        </button>
      </div>
    </div>
  </form>
</template>

<script>
import SelectMonth from '@/components/forms/SelectMonth';
import SelectProducts from '@/components/forms/SelectProducts';
import { returnMessageFormMixin } from '@/mixins/returnMessageFormMixin';
import moment from 'moment';

import { baseApiUrl } from '@/global';
import axios from 'axios';

export default {
  mixins: [returnMessageFormMixin],
  name: 'CustomerContractReadjustmentForm',
  components: { SelectMonth, SelectProducts },
  props: {
    contract: {
      type: Object,
    },
    changeReason: String,
  },
  data() {
    return {
      data: {},
      readjustment_history: [],
      loading: false,
      loadingContracts: false,
      contractList: [],
      showContractList: false,
      currentPage: 1,
      itemsPerPage: 10,
      selectedContractIds: [],
    };
  },
  methods: {
    onMonthChange(selectedMonth) {
      this.data.actual_date_birthday_month = selectedMonth;
      if (selectedMonth && !this.data.applyToAllContracts) {
        this.getContractsByMonth(selectedMonth);
      } else {
        this.showContractList = false;
        this.contractList = [];
        this.selectedContractIds = [];
      }
    },
    toggleSelectAll() {
      if (this.allContractsSelected) {
        this.selectedContractIds = [];
      } else {
        this.selectedContractIds = this.contractList.map(contract => contract.contract_id);
      }
    },
    async getContractsByMonth(month) {
      this.loadingContracts = true;
      this.showContractList = true;

      try {
        const url = `${baseApiUrl}/v1/customer/contract/readjustment`;
        const response = await axios.get(url, {
          params: {
            actual_date_birthday_month: month
          }
        });

        this.contractList = response.data || [];
        this.currentPage = 1;
        this.selectedContractIds = [];

      } catch (error) {
        console.error('Error fetching contracts:', error);
        this.contractList = [];

        this.alert = {
          color: 'alert-danger',
          icon: 'fas fa-exclamation-triangle',
          message: 'Erro ao carregar contratos',
          reason: 'Não foi possível carregar a lista de contratos para o mês selecionado.'
        };
      } finally {
        this.loadingContracts = false;
      }
    },
    async saveForm() {
      const { actual_date_birthday_month } = this.data;
      const found = this.readjustment_history.find(
        (e) => e.birthday_month == actual_date_birthday_month
      );


      const confirmMessage = found
        ? `Último Reajuste de contrato do mês de ${this.getFieldModifiedValue(found.birthday_month)} foi executado anteriormente na data: ${moment(found.event_date).format('DD/MM/YYYY')}. Deseja prosseguir?`
        : `Deseja prosseguir com o reajuste para o mês ${this.getFieldModifiedValue(actual_date_birthday_month)}?`;

      this.$bvModal
        .msgBoxConfirm(confirmMessage, {
          size: 'md',
          contentClass: 'p-3',
          bodyClass: 'modal-body-fix',
          buttonSize: 'md',
          okVariant: 'danger',
          okTitle: 'Confirmar Reajuste',
          cancelTitle: 'Cancelar',
          cancelVariant: 'metal',
          footerClass: 'p-4',
          hideHeaderClose: true,
          centered: true,
        })
        .then(async (value) => {
          if (value == true) {
            this.executeReadjustment();
          }
        });
    },
    async executeReadjustment() {
      await this.messageWaitingSave();

      const { data } = this;

      if (!data.applyToAllContracts && this.selectedContractIds.length > 0) {
        data.contract_ids = this.selectedContractIds;
      }

      const alertEl = this.$refs.alert;

      if (alertEl) {
        alertEl.scrollIntoView();
      }

      this.loading = true;
      const url = data.applyToAllContracts
        ? `${baseApiUrl}/v1/customer/contract/readjustment-all`
        : `${baseApiUrl}/v1/customer/contract/readjustment`;

      axios.post(url, data).then((res) => {
        if (res.data.status == 404) {
          this.loading = false;
          this.logMessageFormFailed(res);
          return;
        }

        this.logMessageForm(res);
        this.$store.state.showContractSave = res.data.item.id;
        this.$emit('savedContract', res.data.item);
        this.loading = false;
      }).catch((error) => {
        this.loading = false;
        this.logMessageFormFailed(error.response);
      });
    },
    getDatas() {
      const url = `${baseApiUrl}/v1/audits/contract-readjustment`;
      axios(url).then((res) => {
        this.readjustment_history = res.data;
      });
    },
    getFieldModifiedValue(month) {
      let stringMonth = month;

      switch (stringMonth) {
        case '0':
          stringMonth = 'Todos';
          break;
        case '1':
          stringMonth = 'Janeiro';
          break;
        case '2':
          stringMonth = 'Fevereiro';
          break;
        case '3':
          stringMonth = 'Março';
          break;
        case '4':
          stringMonth = 'Abril';
          break;
        case '5':
          stringMonth = 'Maio';
          break;
        case '6':
          stringMonth = 'Junho';
          break;
        case '7':
          stringMonth = 'Julho';
          break;
        case '8':
          stringMonth = 'Agosto';
          break;
        case '9':
          stringMonth = 'Setembro';
          break;
        case '10':
          stringMonth = 'Outubro';
          break;
        case '11':
          stringMonth = 'Novembro';
          break;
        case '12':
          stringMonth = 'Dezembro';
          break;
        default:
      }

      return stringMonth;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    getCustomerStatusText(status) {
      switch (status) {
        case 1:
          return 'Ativo';
        case 3:
          return 'Bloqueado';
        default:
          return 'Desconhecido';
      }
    },
    getCustomerStatusClass(status) {
      switch (status) {
        case 1:
          return 'badge badge-success';
        case 3:
          return 'badge badge-warning';
        default:
          return 'badge badge-secondary';
      }
    },
    getStatusClass(status) {
      const statusClasses = {
        'Ativo': 'badge badge-success',
        'Inativo': 'badge badge-danger',
        'Pendente': 'badge badge-warning',
        'Suspenso': 'badge badge-secondary'
      };
      return statusClasses[status] || 'badge badge-primary';
    },
    formatLastReadjustment(contract) {
      if (!contract.last_readjustment_percent || !contract.last_readjustment_date) {
        return '-';
      }

      const percent = contract.last_readjustment_percent.replace('.', ',');

      const date = new Date(contract.last_readjustment_date);
      const formattedDate = date.toLocaleDateString('pt-BR');

      return `${percent}% - ${formattedDate}`;
    },
  },
  computed: {
    contractId() {
      return this.$store.state.showContractSave;
    },
    allContractsSelected() {
      return this.contractList.length > 0 && this.selectedContractIds.length === this.contractList.length;
    },
    formIsValid() {
      if (this.data.applyToAllContracts) {
        return !!this.data.percent;
      }

      return !!(this.data.actual_date_birthday_month && this.data.percent);
    },
    paginatedContracts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.contractList.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.contractList.length / this.itemsPerPage);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        } else if (current >= total - 3) {
          pages.push(1);
          pages.push('...');
          for (let i = total - 4; i <= total; i++) {
            pages.push(i);
          }
        } else {
          pages.push(1);
          pages.push('...');
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i);
          }
          pages.push('...');
          pages.push(total);
        }
      }

      return pages.filter(page => page !== '...' || pages.indexOf(page) === pages.lastIndexOf(page));
    },
  },
  beforeMount() {
    this.data = {
      actual_date_birthday_month: 0,
      percent: 0,
      applyToAllContracts: false,
    };

    this.getDatas();
  },
  watch: {
    changeReason() {
      this.data.change_reason = this.changeReason;
    },
  },
};
</script>

<style>
.badge {
  font-size: 0.85em;
}

.table th {
  border-top: none;
}

.pagination .page-link {
  border-radius: 0.25rem;
  margin: 0 2px;
}

.card-header h5 {
  margin-bottom: 0;
}
</style>
